"use client";

import { useEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import {
  builderStudioFrameMountNodeForDevice,
  builderStudioFrameWidth,
} from "./model";
import { copyBuilderPreviewFontCustomProperties } from "./frameFonts";
import type { BuilderStudioDevice } from "./types";

export type BuilderDeviceFrameProps = {
  children: ReactNode;
  className?: string;
  device: BuilderStudioDevice;
  frameRef?: RefObject<HTMLIFrameElement | null>;
  onFrameDocumentReady?: (frameDocument: Document) => void;
  title: string;
};

const previewRootId = "builder-studio-preview-root";
const clonedStyleAttribute = "data-builder-preview-cloned";

export function BuilderDeviceFrame({
  children,
  className = "",
  device,
  frameRef,
  onFrameDocumentReady,
  title,
}: BuilderDeviceFrameProps) {
  const internalFrameRef = useRef<HTMLIFrameElement | null>(null);
  const resolvedFrameRef = frameRef ?? internalFrameRef;
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
  const viewportWidth = builderStudioFrameWidth(device);

  useEffect(() => {
    setMountNode((current) =>
      builderStudioFrameMountNodeForDevice(device, current));
  }, [device]);

  function syncFrameDocument() {
    const frameDocument = resolvedFrameRef.current?.contentDocument;

    if (!frameDocument) {
      return;
    }

    copyBuilderPreviewFontCustomProperties(
      document.documentElement,
      frameDocument.documentElement,
    );

    frameDocument.head
      .querySelectorAll(`[${clonedStyleAttribute}]`)
      .forEach((node) => node.remove());

    document
      .querySelectorAll<HTMLLinkElement | HTMLStyleElement>(
        'link[rel="stylesheet"], style',
      )
      .forEach((node) => {
        const clone = node.cloneNode(true) as HTMLLinkElement | HTMLStyleElement;
        clone.setAttribute(clonedStyleAttribute, "true");
        frameDocument.head.appendChild(clone);
      });

    const baseStyle = frameDocument.createElement("style");
    baseStyle.setAttribute(clonedStyleAttribute, "true");
    baseStyle.textContent = `html,body,#${previewRootId}{min-height:100%;}body{margin:0;background:#fff;}`;
    frameDocument.head.appendChild(baseStyle);

    const root = frameDocument.getElementById(previewRootId);
    setMountNode(root);
    onFrameDocumentReady?.(frameDocument);
  }

  return (
    <>
      <iframe
        ref={resolvedFrameRef}
        title={title}
        srcDoc={`<!doctype html><html><head></head><body><div id="${previewRootId}"></div></body></html>`}
        className={[
          "mx-auto block h-full max-w-full border-0 bg-white shadow-sm transition-[width] duration-200",
          className,
        ].filter(Boolean).join(" ")}
        data-builder-device-frame={device}
        data-builder-device-viewport={viewportWidth}
        style={{ width: viewportWidth }}
        onLoad={syncFrameDocument}
      />
      {mountNode ? createPortal(children, mountNode) : null}
    </>
  );
}
